import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  ArrowRight,
  Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      toast.success(response.data.message);
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  const services = [
    { name: "Business Growth Strategy", slug: "business-growth-strategy" },
    { name: "Marketing Automation", slug: "marketing-automation" },
    { name: "Sales Funnels & CRM", slug: "sales-funnels-crm" },
    { name: "Lead Generation", slug: "lead-generation" },
    { name: "SEO & Content", slug: "seo-content" },
    { name: "Paid Media", slug: "paid-media" },
    { name: "Web & App Development", slug: "web-app-development" },
    { name: "ChatGPT Ads & GEO", slug: "chatgpt-ads" },
  ];

  const company = [
    { name: "About Us", href: "/about" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Blog", href: "/blog" },
    { name: "Pricing", href: "/pricing" },
    { name: "Client Onboarding", href: "/client-onboarding" },
    { name: "Contact", href: "/contact" },
  ];

  const resources = [
    { name: "Free Guides", href: "/resources" },
    { name: "Growth Systems", href: "/growth-systems" },
    { name: "OptimaGrowth Software", href: "/software" },
  ];

  const legal = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
  ];

  return (
    <footer className="bg-optima-dark text-white" data-testid="main-footer">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="container-custom py-16">
          <div className="bg-gradient-to-r from-optima-blue to-optima-cyan rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                  Ready to Accelerate Your Growth?
                </h3>
                <p className="text-white/90 text-lg">
                  Book a free strategy call with our team and discover how we can help you build systems that scale.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
                  data-testid="footer-cta-call"
                >
                  Book a Free Call <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6" data-testid="footer-logo">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center">
                <span className="text-white font-heading font-bold text-xl">O</span>
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-xl">OptimaGrowth</span>
                <span className="text-[10px] text-white/60 -mt-1 tracking-wider uppercase">
                  Growth Partner
                </span>
              </div>
            </Link>
            <p className="text-white/70 mb-6 leading-relaxed">
              Your strategic growth partner. We build automated systems that generate leads, nurture prospects, and scale your business.
            </p>
            <div className="space-y-3 text-sm">
              <a
                href="mailto:info@optimagrowth.agency"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                info@optimagrowth.agency
              </a>
              <a
                href="tel:+27661229790"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                +27 66 122 9790
              </a>
              <div className="flex items-center gap-3 text-white/70">
                <MapPin className="w-4 h-4" />
                Cape Town, South Africa
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-white">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                    data-testid={`footer-service-${service.slug}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-white">Company</h4>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                    data-testid={`footer-company-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-white">Resources</h4>
            <ul className="space-y-3">
              {resources.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                    data-testid={`footer-resource-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-white">Newsletter</h4>
            <p className="text-white/70 text-sm mb-4">
              Get growth insights delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-optima-cyan"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-optima-blue hover:bg-optima-blue-dark"
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? (
                  <span className="spinner" />
                ) : (
                  <>
                    Subscribe <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/50 text-sm">
              © {new Date().getFullYear()} OptimaGrowth. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              {legal.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-white/50 hover:text-white text-sm transition-colors"
                  data-testid={`footer-legal-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white transition-colors"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
